import type { AssetGroup, IconAsset, IconSourceData, Resolution, Scene } from '@lumen5/luminary';
import Luminary, { VideoData } from '@lumen5/luminary';

import type { SlideBackgroundModel } from 'js/src/client';
import {
    ApiService,
    FreepikLicenseRequestIconTypeEnum,
    LicenseEnum,
    SourceEnum,
} from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import {
    markSceneAsFinishedLoading,
    markSceneAsLoading,
    SCENE_GROUP_LOADING_TYPES,
} from 'js/src/data/app';
import {
    getSceneColorShuffleData,
    getSceneColorShuffleIndex,
    isSceneColorShuffleAvailable,
} from 'js/src/data/color-shuffle';
import type { StoredAssetContainer } from 'js/src/data/media';
import {
    getActiveSearchContextForCurrentTab,
    isMediaSourceData,
    SEARCH_CONTEXT,
} from 'js/src/data/media';
import { getFocusedSubsceneIndex } from 'js/src/data/scenes';
import { getTemplateBundle } from 'js/src/data/templates';
import type { IconAssetContainerModel } from 'js/src/data/uploads';
import { readonlyAssetContainerModelIsIcon } from 'js/src/data/uploads';
import { checkpointVideo } from 'js/src/data/video-data';
import { setObjectPropertyValue } from 'js/src/editor/util';
import { SIMPLE_ICON_COLOR_THRESHOLD, toMutableCopy } from 'js/src/libs/consts'; // we consider icon as simple less than this number of colors
import { focusAssetElement } from 'js/src/libs/custom-utils';
import type { StartEditingNonTextAssetPayload } from 'js/src/libs/slide-event-emitter';
import SlideEventEmitter, { SLIDE_EVENTS } from 'js/src/libs/slide-event-emitter';
import uuid from 'js/src/libs/uuid';
import {
    addStickerAssetToSceneInternalOperation,
    deleteAssetOperation,
    getIconType,
    getOrCreateStickerCompatibleSceneOperation,
    getVoiceDrivenAssetSourceData,
    IconType,
    isTalkingHeadSceneGroup,
    saveRawLuminary,
} from 'js/src/luminary/index';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    setMediaAssetForSceneOperation,
    SetMediaAssetForSceneOperationMethod,
} from 'js/src/luminary/luminary-operations';
import { getElementPropertiesToSave } from 'js/src/luminary/luminary-utils';
import { getVoicedrivenSceneDuration } from 'js/src/luminary/voicedriven-scenes';
import type { ImmutableMap } from 'js/src/luminary/immutable-helpers';

/**
 * Update a color property of an icon asset
 */
export const setIconColorOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        colorPropertyName: string,
        newColor: number | string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) return;
        const asset = scene.getAssetById(assetId);
        if (asset && VideoData.isIconAsset(asset)) {
            setObjectPropertyValue(asset, colorPropertyName, newColor);
        }
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export function buildFreepikIconSourceData(thumbnailUrl: string, sourceId: string): IconSourceData {
    return {
        url: thumbnailUrl,
        uuid: uuid(),
        animated: true,
        source: SourceEnum.SOURCE_FREEPIK,
        source_id: sourceId,
        license: LicenseEnum.CC0,
    };
}

/**
 * Assigns the provided iconSourceData object to the specified scene.
 * If no targetAssetId is specified we'll create the icon as a sticker on the scene
 * Creates a new scene if the scene is not compatible with an icon.
 * Returns the created/updated icon asset, or null if the operation failed.
 */
export const setIconSouceDataForSceneOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        sceneId: string,
        iconSourceData: IconSourceData,
        {
            targetAssetId = null,
            targetPosition = null,
            targetDimensions = null,
            isWaveform = false,
            numColors = 0,
            attribution = '',
            colorMapOverrides = null,
        }: {
            targetAssetId?: string | null;
            targetPosition?: { x: number; y: number } | null;
            targetDimensions?: Resolution | null;
            isWaveform?: boolean;
            numColors?: number;
            attribution?: string;
            colorMapOverrides?: { sourceColor: number; targetColor: number }[] | null;
        } = {},
        systemTriggered = false,
        parentActionGroup = uuid(),
    ): Promise<IconAsset | null | undefined> => {
        // License Freepik assets and get the Lottie JSON URL before applying to scene
        if (iconSourceData.source === SourceEnum.SOURCE_FREEPIK && iconSourceData.source_id) {
            dispatch(
                markSceneAsLoading({ sceneId, type: SCENE_GROUP_LOADING_TYPES.SIMPLE_MESSAGE }),
            );
            try {
                const response = await ApiService.freepikLicenseCreate({
                    body: { source_id: iconSourceData.source_id },
                });
                if (!response.data?.licensed) {
                    Toast.error('Failed to apply sticker, please try again', 'Error');
                    return;
                }
                if (response.data.lottie_url) {
                    iconSourceData = {
                        ...iconSourceData,
                        url: response.data.lottie_url,
                    };
                }
            } catch {
                Toast.error('Failed to apply sticker, please try again', 'Error');
                return;
            } finally {
                dispatch(markSceneAsFinishedLoading({ sceneId }));
            }
        }

        const scene = call(
            getOrCreateStickerCompatibleSceneOperation,
            sceneId,
            systemTriggered,
            parentActionGroup,
        );
        if (!scene) {
            Toast.error('Unable to add icon to scene', 'Error');
            return null;
        }

        const tryGetOrAddIconAsset = (scene: Scene) => {
            let iconAsset: IconAsset | null = null;
            const targetAsset = targetAssetId ? scene.getAssetById(targetAssetId) : undefined;
            let introOutroTimes = null;
            let dynamicStyles = null;
            let groupIndex = null;
            let container: Scene | AssetGroup = scene;
            if (targetAsset) {
                if (VideoData.isIconAsset(targetAsset)) {
                    const swappable = isWaveform
                        ? getIconType(targetAsset) === IconType.WAVEFORM
                        : true;
                    if (swappable) {
                        iconAsset = targetAsset;
                    }
                } else if (
                    VideoData.isStickerAsset(targetAsset) &&
                    VideoData.isMediaAsset(targetAsset)
                ) {
                    const assetGroup = targetAsset.getParentByType(VideoData.GROUP_ASSET_TYPE);
                    if (assetGroup) {
                        container = assetGroup;
                    }
                    ({
                        position: targetPosition,
                        dimensions: targetDimensions,
                        introOutroTimes,
                        dynamicStyles,
                        groupIndex,
                    } = getElementPropertiesToSave(
                        targetAsset,
                        getVoicedrivenSceneDuration(scene),
                    ));
                    call(deleteAssetOperation, scene.id, targetAsset.id);
                    iconAsset = null;
                }
            }

            if (!iconAsset) {
                iconAsset = call(
                    addStickerAssetToSceneInternalOperation,
                    container,
                    VideoData.ICON_ASSET_TYPE,
                    {
                        position: targetPosition,
                        dimensions: targetDimensions,
                        introOutroTimes,
                        dynamicStyles,
                        groupIndex,
                    },
                    systemTriggered,
                    parentActionGroup,
                );
            }

            if (iconAsset && isWaveform) {
                const sceneGroup = scene.sceneGroup;
                if (!sceneGroup) {
                    return null;
                }
                if (isTalkingHeadSceneGroup(sceneGroup)) {
                    const talkingHeadSourceData = getVoiceDrivenAssetSourceData(sceneGroup);
                    if (isMediaSourceData(talkingHeadSourceData)) {
                        if (!talkingHeadSourceData.audio_url) {
                            const toastDuration = 10000;
                            Toast.error(
                                'Re-upload your video file to use the waveform',
                                'Something went wrong',
                                'waveform-missing-audio-id',
                                toastDuration,
                            );
                            analytics.track(
                                'Waveform added to talking head scene with missing audio_url',
                            );
                        }
                    }
                }
                iconAsset.playbackMode = VideoData.PLAYBACK_WAVEFORM;
                // TODO: Please remove the line below once luminary merges this ticket:
                // https://lumen5.atlassian.net/browse/LU-3877
                scene.requiresUpdate = true;
            }
            return iconAsset;
        };

        // get the icon to update
        const iconAsset = tryGetOrAddIconAsset(scene);
        if (!iconAsset) {
            Toast.error('Unable to add icon to scene', 'Error');
            return null;
        }

        // assign sourceData
        if (iconSourceData.source === SourceEnum.SOURCE_FREEPIK) {
            // Freepik stickers keep their own baked-in colors — don't inherit the replaced asset's colorMap
            iconAsset.sourceData = iconSourceData;
        } else if (
            numColors === 0 &&
            iconSourceData.source === SourceEnum.SOURCE_LUMEN5_ICONS_PREMIUM
        ) {
            // some of uploaded lumen5 icons white and doesn't have num_colors value in DB, so we need to replace sourceData to apply existing colorMap
            iconAsset.replace(iconSourceData); // will use old colorMap
        } else if (
            numColors < SIMPLE_ICON_COLOR_THRESHOLD &&
            iconAsset.colorMaps.length === numColors
        ) {
            // basic case: simple icon replace with similar simple icon
            iconAsset.replace(iconSourceData); // if icon has less than 3 colors rewrite colors with old colorMap
        } else if (
            numColors < SIMPLE_ICON_COLOR_THRESHOLD &&
            iconAsset.colorMaps.length > SIMPLE_ICON_COLOR_THRESHOLD
        ) {
            const state = getState();
            const isColorShuffleAvaliable = isSceneColorShuffleAvailable(state, sceneId);
            // we're using a "data" Luminary instance, an instance of Luminary which we're configuring to be fast so we can use it to pull info
            // Even with these optimizations it takes 30-50ms to create so we shouldn't abuse it!
            const dataLuminary = new Luminary({
                fetchResources: false,
                disableRenderer: true,
                autoDraw: false,
                autoUpdate: false,
            });

            const templateBundle = getTemplateBundle(state, luminary.template.id);

            dataLuminary.setTemplate(templateBundle!);

            const targetSceneDesignId = scene.sceneDesign;
            const dataScene = dataLuminary.addScene({
                sceneDesignId: targetSceneDesignId,
            });

            // now we can just pull data from the data scene!
            const templateIconAsset = dataScene.getAssetByName(iconAsset.name) as IconAsset;
            if (templateIconAsset) {
                let iconIndexColor = VideoData.getTemplateIndexFromAssetProperty(
                    templateIconAsset,
                    'colorMaps.0.targetColor',
                );

                if (isColorShuffleAvaliable) {
                    const colorShuffleIndex = getSceneColorShuffleIndex(state, sceneId);
                    const colorShuffleData = getSceneColorShuffleData(state, sceneId);
                    if (colorShuffleData) {
                        iconIndexColor =
                            colorShuffleData.colorShuffles[colorShuffleIndex][iconIndexColor];
                    }
                }
                setObjectPropertyValue(
                    iconAsset,
                    'colorMaps[0].targetColor',
                    VideoData.templateColorByIndex(iconIndexColor),
                ); // we modify old map to use original color from template and then replace icons.
            }

            iconAsset.replace(iconSourceData);

            dataLuminary.destroy();
        } else {
            iconAsset.sourceData = iconSourceData; // this will create new colorMap from icon using custom colors
            iconAsset.slide!.attribution = attribution;
        }

        if (colorMapOverrides && colorMapOverrides.length > 0) {
            iconAsset._colorMapsCustomLoad(colorMapOverrides, {});
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        if (!systemTriggered) {
            const subsceneIndex = getFocusedSubsceneIndex(getState());
            const sceneGroup = scene.sceneGroup;
            if (!sceneGroup) {
                return null;
            }
            SlideEventEmitter.emit(SLIDE_EVENTS.START_EDITING_NON_TEXT_ASSET, {
                sceneGroupId: sceneGroup.id,
                sceneId,
                subsceneIndex,
                assetId: iconAsset.id,
                assetType: VideoData.ICON_ASSET_TYPE,
            } as StartEditingNonTextAssetPayload);
            focusAssetElement(iconAsset.id, sceneId, subsceneIndex ?? 0);
        }

        return iconAsset;
    },
);

export const applyFreepikStaticStickerOperation = createLuminaryOperation(
    async (
        { call, dispatch, luminary },
        sceneId: string,
        background: ImmutableMap<SlideBackgroundModel>,
        sourceId: string,
        {
            targetAssetId = null,
            targetPosition = null,
            method = SetMediaAssetForSceneOperationMethod.CLICK,
            defaultSize,
        }: {
            targetAssetId?: string | null;
            targetPosition?: { x: number; y: number } | null;
            method?: SetMediaAssetForSceneOperationMethod;
            defaultSize?: number;
        } = {},
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const size = defaultSize ?? Math.round(luminary.referenceResolution.height / 3);

        dispatch(markSceneAsLoading({ sceneId, type: SCENE_GROUP_LOADING_TYPES.SIMPLE_MESSAGE }));
        let svgUrl: string | undefined;
        try {
            const response = await ApiService.freepikLicenseCreate({
                body: { source_id: sourceId, icon_type: FreepikLicenseRequestIconTypeEnum.STATIC },
            });
            if (response.data?.licensed) {
                svgUrl = response.data.svg_url;
            }
        } catch {
            // fall through to error toast
        } finally {
            dispatch(markSceneAsFinishedLoading({ sceneId }));
        }
        if (!svgUrl) {
            Toast.error('Failed to apply sticker, please try again', 'Error');
            return null;
        }
        const updatedBackground = background
            .setIn(['image', 'thumbnail_url'], svgUrl)
            .setIn(['image', 'preview_url'], svgUrl)
            .setIn(['image', 'render_url'], svgUrl)
            .setIn(['image', 'width'], size)
            .setIn(['image', 'height'], size);
        return call(setMediaAssetForSceneOperation, {
            sceneId,
            mediaSourceData: updatedBackground,
            targetAssetId,
            method,
            isSticker: true,
            targetPosition: targetPosition ?? undefined,
            systemTriggered,
            parentActionGroup,
        });
    },
);

/**
 * Pulls the icon source data out of the asset container and calls {@link setIconSouceDataForSceneOperation}
 */
export const setIconAssetForSceneOperation = createLuminaryOperation(
    (
        { call, getState },
        sceneId: string,
        iconAssetContainer: StoredAssetContainer,
        {
            targetAssetId = null,
            targetPosition = null,
            targetDimensions = null,
            isDrag = false,
        }: {
            targetAssetId?: string | null;
            targetPosition?: { x: number; y: number } | null;
            targetDimensions?: Resolution | null;
            isDrag?: boolean;
        } = {},
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        if (!readonlyAssetContainerModelIsIcon(iconAssetContainer)) {
            Toast.error('Invalid icon source data', 'Error');
            return;
        }

        const rawSourceData = toMutableCopy<IconAssetContainerModel>(iconAssetContainer);
        const searchContext = getActiveSearchContextForCurrentTab(getState());

        const isWaveform = searchContext === SEARCH_CONTEXT.WAVEFORMS;

        call(
            setIconSouceDataForSceneOperation,
            sceneId,
            rawSourceData.icon as IconSourceData,
            {
                targetAssetId,
                targetPosition,
                targetDimensions,
                attribution: rawSourceData.icon.attribution ?? '',
                isWaveform,
            },
            systemTriggered,
            parentActionGroup,
        );
        analytics.track('Icon Added To Scene', {
            sceneId: sceneId,
            method: isDrag ? 'drag' : 'click',
            media_source: searchContext,
            current_user: rawSourceData.icon.history_index_user_ids?.some(
                (user_id: number) => SERVER.USER.id === user_id,
            ),
            is_waveform: isWaveform,
        });
    },
);
